import React, { useState, useEffect } from "react";
import { Dialog, TextField } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DropdownPill from "./DropdownPill";
import api from "../api/api";
import ToastMessage from "../utils/mui/righttoptoast";
import dayjs from "dayjs";
import { getPracticeId } from "../config/practice";

const RequestTimeOffModal = ({ open, user, fetchCount, onClose }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [requestType, setRequestType] = useState("");
  const [reason, setReason] = useState("");
  const [workCoverage, setWorkCoverage] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [totalHours, setTotalHours] = useState("");
  const [loading, setLoading] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);

  const practiseid = getPracticeId();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setStartDate(null);
      setEndDate(null);
      setRequestType("");
      setReason("");
      setWorkCoverage("");
      setEmergencyContact("");
      setTotalHours("");
      setLoading(false);
    }
  }, [open]);

  // Auto-calculate hours (8 per day)
  useEffect(() => {
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);

      if (end.isAfter(start) || end.isSame(start)) {
        const diffDays = end.diff(start, "day") + 1;
        setTotalHours(String(diffDays * 8));
      }
    }
  }, [startDate, endDate]);

  // Disable weekends
  const disableWeekends = (date) => {
    const day = date.day();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        emp_id: user.id,
        emp_name: `${user.firstname} ${user.lastname}`,
        emp_role: user.role,
        employee_type: user?.config?.emp_type,
        start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
        end_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
        request_type: requestType,
        total_hours: totalHours,
        reason,
        work_coverage: workCoverage,
        emergency_contact: emergencyContact,
      };

      const response = await api.post(`/v1/${practiseid}/pto/request`, payload);

      if (response.status === 200) {
        setToastMessage("Time off request submitted successfully!");
        setToastSeverity("success");
        setOpenToast(true);
        fetchCount();
        setTimeout(() => onClose(), 1000);
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("❌ Error submitting PTO request:", error);
      setToastMessage("Failed to submit time off request.");
      setToastSeverity("error");
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className="bg-white rounded-lg">
        <div className="px-6 pt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl text-[#1F2937] font-semibold font-inter">
              Request Time Off
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              &times;
            </button>
          </div>
          <div className="text-[#6B7280] font-normal font-inter text-sm mb-3">
            Submit your paid time off request
          </div>
        </div>

        <div className="flex flex-col border-t border-gray-100 pt-2 px-6 pb-6">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit} className="w-full space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Start Date */}
                <div className="flex flex-col">
                  <label className="text-xs font-inter font-medium text-[#374151] mb-1">
                    Start Date
                  </label>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                      if (endDate && dayjs(newValue).isAfter(dayjs(endDate))) {
                        setEndDate(null);
                      }
                    }}
                    shouldDisableDate={disableWeekends}
                    disablePast
                  slotProps={{
    textField: {
      fullWidth: true,
      size: "small",
      className:
        "rounded-xl !border !border-gray-300 focus:!border-gray-300 hover:!border-gray-300 !shadow-none",
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px",
          "& fieldset": {
            borderColor: "#D1D5DB",
          },
          "&:hover fieldset": {
            borderColor: "#D1D5DB",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#D1D5DB", // remove focus blue
          },
        },
        "& .MuiOutlinedInput-input": {
          padding: "8px 10px",
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          color: "#374151",
        },
        "& .MuiInputBase-root": {
          backgroundColor: "#FFFFFF",
        },
      },
    },
  }}
                  />
                </div>

                {/* End Date */}
                <div className="flex flex-col">
                  <label className="text-xs font-inter font-medium text-[#374151] mb-1">
                    End Date
                  </label>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    shouldDisableDate={disableWeekends}
                    disablePast
                    minDate={startDate || dayjs()}
                    disabled={!startDate}
                   slotProps={{
    textField: {
      fullWidth: true,
      size: "small",
      className:
        "rounded-xl !border !border-gray-300 focus:!border-gray-300 hover:!border-gray-300 !shadow-none",
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px",
          "& fieldset": {
            borderColor: "#D1D5DB",
          },
          "&:hover fieldset": {
            borderColor: "#D1D5DB",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#D1D5DB", // remove focus blue
          },
        },
        "& .MuiOutlinedInput-input": {
          padding: "8px 10px",
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          color: "#374151",
        },
        "& .MuiInputBase-root": {
          backgroundColor: "#FFFFFF",
        },
      },
    },
  }}
                  />
                </div>
              </div>

              {/* Request Type */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-inter font-medium text-[#374151]">
                  Request Type
                </label>
                <DropdownPill
                  value={requestType}
                  onChange={(val) => setRequestType(val)}
                  placeholder="Select request type"
                  options={[
                    { label: "Vacation", value: "Vacation" },
                    { label: "Sick Leave", value: "Sick Leave" },
                    { label: "Family Emergency", value: "Emergency" },
                    { label: "Other", value: "Other" },
                  ]}
                  className="w-full text-[#000]"
                />
              </div>

              {/* Reason */}
              <div className="flex flex-col">
                <label className="text-xs font-inter font-medium text-[#374151]">
                  Reason (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows="3"
                  placeholder="Briefly describe the reason..."
                  className="p-2 border border-gray-300 rounded-xl focus:outline-none"
                />
              </div>

              {/* Work Coverage */}
              <div className="flex flex-col">
                <label className="text-xs font-inter font-medium text-[#374151]">
                  Work Coverage Arrangements
                </label>
                <textarea
                  value={workCoverage}
                  onChange={(e) => setWorkCoverage(e.target.value)}
                  rows="3"
                  placeholder="Describe how your responsibilities will be handled..."
                  className="p-2 border border-gray-300 rounded-xl focus:outline-none"
                />
              </div>

              {/* Emergency Contact */}
              <div className="flex flex-col">
                <label className="text-xs font-inter font-medium text-[#374151]">
                  Emergency Contact (Optional)
                </label>
                <input
                  type="text"
                  value={emergencyContact}
                  onChange={(e) => setEmergencyContact(e.target.value)}
                  placeholder="Name and phone number"
                  className="p-2 border border-gray-300 rounded-xl focus:outline-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end items-center mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="bg-info-300 text-gray-700 px-4 py-2 m-3 rounded-md border border-gray-300 hover:bg-info-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`text-white px-4 py-2 m-3 rounded-md ${
                    loading ? "bg-gray-400" : "bg-[#202224]"
                  }`}
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </LocalizationProvider>
        </div>
      </div>

      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={() => setOpenToast(false)}
      />
    </Dialog>
  );
};

export default RequestTimeOffModal;
